'use strict';

const HIDDEN_CLASS = 'u-hidden';
const TOOLTIP_OPEN_CLASS = 'page-feedback__info--is-open';
const FORM_OPEN_CLASS = 'page-feedback--form-open';
const MAX_LENGTH = 500;
const ENDPOINT = '/api/page-feedback';

export default class PageFeedback {
    constructor( pageFeedback ) {
        this.container = pageFeedback;
        this.intro = this.container.querySelector( '[data-js="page-feedback-intro"]' );
        this.toggle = this.container.querySelector( '[data-js="page-feedback-toggle"]' );
        this.closeToggle = this.container.querySelector( '[data-js="page-feedback-close"]' );
        this.form = this.container.querySelector( '[data-js="page-feedback-form"]' );
        this.options = this.container.querySelectorAll( '[data-js="page-feedback-option"]' );
        this.input = this.container.querySelector( '[data-js="page-feedback-input"]' );
        this.message = this.container.querySelector( '[data-js="page-feedback-message"]' );
        this.counter = this.container.querySelector( '[data-js="page-feedback-counter"]' );
        this.submit = this.container.querySelector( '[data-js="page-feedback-submit"]' );
        this.honeypot = this.container.querySelector( '[data-js="page-feedback-honeypot"]' );
        this.infoWrapper = this.container.querySelector( '[data-js="page-feedback-info-wrapper"]' );
        this.infoButton = this.container.querySelector( '[data-js="page-feedback-info"]' );
        this.confirmation = this.container.querySelector( '[data-js="page-feedback-confirmation"]' );
        this.error = this.container.querySelector( '[data-js="page-feedback-error"]' );
        this.retry = this.container.querySelector( '[data-js="page-feedback-retry"]' );

        this.init();
    }

    init() {
        if ( this.form === null ) {
            return;
        }

        this.toggle.addEventListener( 'click', this.showForm.bind( this ) );
        this.closeToggle.addEventListener( 'click', this.hideForm.bind( this ) );
        this.form.addEventListener( 'submit', this.handleSubmit.bind( this ) );
        this.message.addEventListener( 'input', this.handleInput.bind( this ) );
        this.infoButton.addEventListener( 'click', this.toggleTooltip.bind( this ) );
        this.retry.addEventListener( 'click', this.reopenForm.bind( this ) );
        document.addEventListener( 'click', this.handleDocumentClick.bind( this ) );
        document.addEventListener( 'keydown', this.handleDocumentKeydown.bind( this ) );

        for ( const option of this.options ) {
            option.addEventListener( 'change', this.showInput.bind( this ) );
        }

        this.updateState();
    }

    hideForm() {
        this.form.hidden = true;
        this.toggle.hidden = false;
        this.toggle.setAttribute( 'aria-expanded', 'false' );
        this.closeToggle.hidden = true;
        this.closeToggle.setAttribute( 'aria-expanded', 'false' );
        this.container.classList.remove( FORM_OPEN_CLASS );
        this.setTooltipVisibility( false );
        this.toggle.focus();
    }

    showForm() {
        this.form.hidden = false;
        this.toggle.setAttribute( 'aria-expanded', 'true' );
        this.toggle.hidden = true;
        this.closeToggle.hidden = false;
        this.closeToggle.setAttribute( 'aria-expanded', 'true' );
        this.container.classList.add( FORM_OPEN_CLASS );
    }

    showInput() {
        this.input.classList.remove( HIDDEN_CLASS );
        this.message.disabled = false;
        this.updateState();
    }

    handleInput() {
        if ( this.message.value.length > MAX_LENGTH ) {
            this.message.value = this.message.value.substring( 0, MAX_LENGTH );
        }

        this.updateState();
    }

    handleSubmit( event ) {
        event.preventDefault();

        if ( false === ( this.hasSelectedOption() && this.hasComment() ) ) {
            this.focusInvalidField();
            return;
        }

        this.submit.disabled = true;
        this.sendFeedback();
    }

    sendFeedback() {
        const payload = {
            // eslint-disable-next-line camelcase
            page_url: window.location.href,
            // eslint-disable-next-line camelcase
            feedback_type: this.getSelectedOption(),
            comment: this.message.value.trim(),
            website: this.honeypot === null ? '' : this.honeypot.value
        };

        const request = new XMLHttpRequest();

        request.onreadystatechange = () => {
            if ( request.readyState !== XMLHttpRequest.DONE ) {
                return;
            }

            if ( request.status >= 200 && request.status < 300 ) {
                this.showConfirmation();
            }
            else {
                this.showError();
            }
        };

        request.onerror = () => this.showError();

        request.open( 'POST', ENDPOINT, true );
        request.setRequestHeader( 'Content-Type', 'application/json' );
        request.send( JSON.stringify( payload ) );
    }

    showConfirmation() {
        this.intro.hidden = true;
        this.toggle.hidden = true;
        this.closeToggle.hidden = true;
        this.closeToggle.setAttribute( 'aria-expanded', 'false' );
        this.form.hidden = true;
        this.error.hidden = true;
        this.confirmation.hidden = false;
        this.container.classList.remove( FORM_OPEN_CLASS );
        this.confirmation.focus();
    }

    showError() {
        this.form.hidden = true;
        this.confirmation.hidden = true;
        this.error.hidden = false;
        this.container.classList.remove( FORM_OPEN_CLASS );
        this.error.focus();
    }

    reopenForm() {
        this.error.hidden = true;
        this.intro.hidden = false;
        this.toggle.hidden = true;
        this.closeToggle.hidden = false;
        this.closeToggle.setAttribute( 'aria-expanded', 'true' );
        this.form.hidden = false;
        this.container.classList.add( FORM_OPEN_CLASS );
        this.updateState();
        this.message.focus();
    }

    toggleTooltip( event ) {
        event.stopPropagation();
        this.setTooltipVisibility( !this.infoWrapper.classList.contains( TOOLTIP_OPEN_CLASS ) );
    }

    handleDocumentClick( event ) {
        if ( this.infoWrapper.contains( event.target ) ) {
            return;
        }

        this.setTooltipVisibility( false );
    }

    handleDocumentKeydown( event ) {
        if ( event.keyCode === 27 ) {
            this.setTooltipVisibility( false );
        }
    }

    setTooltipVisibility( showTooltip ) {
        if ( showTooltip ) {
            this.infoWrapper.classList.add( TOOLTIP_OPEN_CLASS );
        }
        else {
            this.infoWrapper.classList.remove( TOOLTIP_OPEN_CLASS );
        }

        this.infoButton.setAttribute( 'aria-expanded', showTooltip.toString() );
    }

    updateState() {
        this.counter.textContent = this.message.value.length + '/' + MAX_LENGTH;
        this.submit.disabled = false === ( this.hasSelectedOption() && this.hasComment() );
    }

    hasSelectedOption() {
        for ( const option of this.options ) {
            if ( option.checked ) {
                return true;
            }
        }

        return false;
    }

    hasComment() {
        return this.message.value.trim().length > 0;
    }

    getSelectedOption() {
        for ( const option of this.options ) {
            if ( option.checked ) {
                return option.value;
            }
        }

        return '';
    }

    focusInvalidField() {
        if ( this.options.length > 0 && false === this.hasSelectedOption() ) {
            this.options[ 0 ].focus();
            return;
        }

        this.message.focus();
    }
}
